import { useBasePath } from '../../../../lib/use-base-path';
import { resolveCreditsMeter, useCredits } from '../../../../lib/use-credits';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useCreditsBiFields, useCreditsState } from '../../../../lib/use-credits-widget-state';
import { useFreeUsageUntil } from '../../../../lib/use-free-usage';
import { useBlackFridayPlanTitle } from '../../../../lib/use-black-friday-plan-title';
import { formatFreeUntil } from '../../../../lib/free-usage';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { skipChatUrlSyncOnce } from '../../FloatingChat/use-chat-url';
import { routerHandlesClick, type ClickModifiers } from '../../../../lib/use-in-app-link';
import { useTranslation } from '../../../../lib/i18n';
import { usePlans, usePremium } from '../../../settings/shared/settings-queries';
import { activePlanIsMaxPlus, activePlanTopup } from '../../../settings/shared/max-plus';
import { activePremiumSubscription } from '../../../settings/shared/settings-plan';
import { planNameKeyOf } from '../../../settings/shared/settings-shared';
import type { PickerTopup } from '../../../../lib/plans';
import type { ProgressLevel } from '../../ui';
import { upgradeHref, withUpgradeOrigin } from '../../../../lib/upgrade-origin';

export interface CreditsSummary {
  planLabel: string;
  /** "70 of 150 monthly credits used" (or "… daily …" when today is the chosen pool) — localized and number-formatted. */
  usageLabel: string;
  /** Credits SPENT as a 0–1 fraction of the cap, for the bar's width — so the bar fills
   *  as the allowance is consumed, matching the settings meter and the native apps. */
  fraction: number;
  /** The DS Progress Bar level. Ink at every level (Figma 15375-104636) — the card's CTA carries the
   *  brand green now, so the bar reports the quantity without grading it. */
  progress: ProgressLevel;
  /**
   * The card's ONE call to action, and there are two of them because the top tier has nothing left to
   * sell:
   *
   *  - `packages` — every plan below the top tier. Navigates to the picker, the SAME destination as the
   *    Account page's own Upgrade button (iOS/Android `CreditsUpgrade`). A real link, so cmd-click and
   *    "open in new tab" work.
   *  - `topup` — the top tier WITH a top-up pack. Opens Buy-more-credits in place (`buyCredits` below
   *    carries what that surface needs), reading "Get more credits" — the same words, from the same
   *    key, that both account cards' top-up uses.
   *
   * A top-tier account with NO pack falls back to `packages` reading "Switch": there is nothing to add,
   * and a button that opens an empty top-up sheet is worse than one that opens the picker.
   */
  upgrade:
    | { kind: 'packages'; route: string; path: string; label: string; onClick: (event?: ClickModifiers) => void }
    | { kind: 'topup'; label: string; onClick: () => void };
  /**
   * The `additionalFields` the card's upgrade click sends, minus its destination `url` — so another
   * credits CTA on the same widget (the daily wall's "Verify your phone") reports exactly what Upgrade
   * does, under its own `button_name`.
   */
  clickBiFields: Record<string, string>;
  /** What the Buy-more-credits surface needs. Non-null exactly when `upgrade.kind === 'topup'`. */
  buyCredits: { topup: PickerTopup; planName: string; checkoutHref: string } | null;
  title: string;
}

// The credits card's numbers and words: plan name, usage line and how full the bar is.
// Returns null while the balance is unknown or the caller has no metasite — there is
// nothing honest to show then. Shared by the wide rail's card and the mobile drawer's.
export function useCreditsSummary(): CreditsSummary | null {
  const { t, i18n } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  // The FT-independent credits-state fields (`credits_state` / `credits_pool` / `plan_tier`) ride EVERY
  // credits CTA, consistent with the personal-sale banner — one source of truth. Called before the early
  // returns below (hooks rule).
  const creditsBiFields = useCreditsBiFields();
  const credits = useCredits();
  // Picks which pool the bar shows (`pickCreditsWidgetPool`) — the same answer the low/out card reads.
  const creditsState = useCreditsState();
  const freeUntil = useFreeUsageUntil();
  // While the campaign's free hours run the card is titled for the campaign, not for the plan.
  const blackFridayTitle = useBlackFridayPlanTitle();
  // The Max+ signal for the drawer's CTA (Change vs Upgrade). The sidebar persists across SPA
  // navigations, so these two reads fetch once per load and stay cached — no per-page cost. Using
  // the SAME catalog rule as the account card (activePlanIsMaxPlus) keeps the two surfaces in step.
  const { data: premium = null } = usePremium();
  const { data: plans = null } = usePlans();
  const { loading, failed, hasMetaSite, account } = credits;
  if (loading || failed || hasMetaSite === false || !account) return null;

  const isMaxPlus = activePlanIsMaxPlus(premium, plans);
  // The top tier's card sells a top-up instead of a plan — but only if the catalog actually hangs a
  // pack off the account's option. Same helper the account cards gate their own top-up button on.
  const topup = isMaxPlus ? activePlanTopup(premium, plans) : null;

  const { isFreePlan } = account;
  // The plan's own NAME, exactly as both native drawers read it (`creditsPlanDisplayName`): the catalog
  // name of the active subscription. Web used to say a generic "Paid Plan" here while the phones named
  // the plan — so one account read "Paid Plan" in the menu and "Max" on the account page.
  //
  // When the catalog cannot name it — the plan left the catalog, or it never was ours (a legacy Wixel
  // "Max" funds the account through the legacy wallet, with no Stripe subscription to key on) — the
  // overview's own `plan.name` is the next-best answer: it is the very plan whose PAID kind put us on
  // this branch, and the exact string native shows. Only when the BFF sends no name either does the
  // generic "Your plan" label stand in; it names nothing, so it is last (native falls back to the FREE
  // label there, which would misreport a paying user).
  const paidPlanNameKey = planNameKeyOf(plans, activePremiumSubscription(premium)?.planId ?? null);
  // (`: string` matters: without a contextual type, the i18n `t` generic infers a nullable return
  // for the dynamic-key call, and the whole chain types as `string | null | undefined`.)
  const paidPlanName: string = paidPlanNameKey ? t(paidPlanNameKey) : account.planName || t('settings.plan.yourPlan');
  // No meter means the balance is unknown, and the card is nothing but the balance — so it stays away
  // rather than reading "0 of 0 monthly credits used", which is what an exhausted account looks like.
  const meter = resolveCreditsMeter(credits);
  // While promo free hours run the card says so instead of measuring a pool nothing is charged
  // against — an empty bar under "Free until …", never a full one.
  if (!meter && !freeUntil) return null;
  const cap = meter?.cap ?? 0;
  const used = meter?.used ?? 0;
  // The bar and its line follow the chosen pool, so an account further through today than through its
  // month reads today's meter here — the same pool its low/out card would warn about. The BI fields
  // below stay on the period meter: they describe the plan, not the bar.
  const showDaily = creditsState.pool === 'daily' && creditsState.cap > 0;
  const barUsed = showDaily ? creditsState.used : used;
  const barCap = showDaily ? creditsState.cap : cap;
  const fraction = freeUntil ? 0 : barCap > 0 ? Math.min(1, Math.max(0, barUsed / barCap)) : 0;
  const clickBiFields: Record<string, string> = {
    ...creditsBiFields,
    plan: isFreePlan ? 'free' : 'paid',
    plan_credits: String(cap),
    remain_credits: String(Math.max(0, cap - used)),
  };

  return {
    planLabel: blackFridayTitle ?? (isFreePlan ? t('sidebar.credits.freePlan') : paidPlanName),
    usageLabel: freeUntil
      ? t('credits.freeUntil', { date: formatFreeUntil(freeUntil, i18n.language) })
      : t(showDaily ? 'sidebar.credits.usedDaily' : 'sidebar.credits.used', { used: barUsed.toLocaleString(), cap: barCap.toLocaleString() }),
    fraction,
    progress: 'neutral',
    title: t('sidebar.credits'),
    upgrade: topup
      ? {
          kind: 'topup',
          // The SAME words the account cards' top-up uses, from the same key. The drawer briefly had a
          // shorter "Add credit" of its own to fit the 236px rail; one string for one action is worth
          // more than the few pixels, and two keys holding the same words is how they drift apart.
          label: t('settings.plan.getMoreCredits'),
          onClick: () => {
            // `get_more_credits`, the SAME button_name both account cards' top-up fires, so the top-up
            // funnel joins across surfaces rather than hiding inside the drawer's `upgrade_credits`.
            // No `url`: this opens a surface in place, so there is no destination to report. And no
            // `markNavFromDrawer`/`leaveChat` — nothing navigates, and closing the chat under a modal
            // would throw away what the user was doing to sell them credits.
            logPageClickBi({ page_name: 'menu', button_name: 'get_more_credits', origin: 'menu' }, { additionalFields: clickBiFields });
          },
        }
      : {
          kind: 'packages',
          route: upgradeHref('sidebar_upgrade'),
          path: withUpgradeOrigin(`${basePath}/packages`, 'sidebar_upgrade'),
          // The top tier reads "Switch" — there is nothing above it to upgrade TO — and every other
          // plan, paid or free, reads "Upgrade". One rule, shared with both Settings surfaces. (A top
          // tier reaching this arm is one whose option carries no top-up pack.)
          label: isMaxPlus ? t('settings.plan.switchPlan') : t('sidebar.credits.upgrade'),
          onClick: (event) => {
            markNavFromDrawer();
            // The four fields the BI review asked for on this click: where Upgrade goes, which plan the
            // user is leaving, and the credit position they leave it at. None has a registered src:172
            // field, so they ride `additionalFields` — same four keys as the native apps'
            // `upgrade_credits`, so the three surfaces' upgrade funnels join.
            logPageClickBi(
              { page_name: 'menu', button_name: 'upgrade_credits', origin: 'menu' },
              {
                additionalFields: { ...clickBiFields, url: `${basePath}/packages` },
              },
            );
            // Only for a same-tab navigation — a modified/middle click opens `/packages` in a NEW
            // tab, so the CURRENT tab never navigates and its own chat-collapse still needs the sync
            // this would otherwise suppress. Before `leaveChat()`, not after — see `skipChatUrlSyncOnce`
            // for why the order matters.
            if (routerHandlesClick(event ?? {})) skipChatUrlSyncOnce();
            leaveChat();
          },
        },
    clickBiFields,
    buyCredits: topup ? { topup, planName: paidPlanName, checkoutHref: withUpgradeOrigin(`${basePath}/packages`, 'sidebar_topup', { topUpPackageId: topup.packId }) } : null,
  };
}
